import React, { useState } from "react";
import { Menu, LogOut, User as UserIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { useAuth } from "../../auth/AuthContext";
import { cn } from "../../lib/utils";

/**
 * Top bar for the app shell — hamburger (mobile), workspace switcher,
 * engine chip (populated on run-detail pages via context; falls back to
 * a neutral chip elsewhere), user menu.
 */
export function TopBar({ onOpenSidebar, engineMeta }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white/95 backdrop-blur px-4"
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onOpenSidebar}
          aria-label="Open menu"
          className="lg:hidden text-slate-600 hover:text-slate-900"
        >
          <Menu size={18} />
        </button>
        <WorkspaceSwitcher />
      </div>
      <div className="flex items-center gap-3">
        {engineMeta && (
          <div
            data-testid="engine-chip"
            className="hidden sm:flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600"
            title={`Rule library ${engineMeta.rule_library_version || "?"}  ·  Hash ${engineMeta.rule_library_hash || "?"}`}
          >
            <span className="uppercase tracking-wider text-slate-400">Engine</span>
            <span className="font-mono text-slate-800">
              {engineMeta.engine_version || "0.0.0"}
            </span>
          </div>
        )}
        <Link
          to="/methodology"
          data-testid="topbar-methodology"
          className="hidden md:inline-block text-xs text-slate-500 hover:text-slate-900"
        >
          Methodology
        </Link>
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="user-menu-btn"
            className="flex items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1 text-xs hover:border-slate-300"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[10px] font-semibold text-white uppercase">
              {(user?.email || "?").slice(0, 1)}
            </span>
            <span className="hidden sm:inline text-slate-700 max-w-[10rem] truncate">
              {user?.email}
            </span>
          </button>
          {menuOpen && (
            <div
              role="menu"
              data-testid="user-menu"
              className="absolute right-0 z-50 mt-1 w-56 overflow-hidden rounded border border-slate-200 bg-white shadow-lg"
            >
              <div className="px-3 py-2 border-b border-slate-100">
                <div className="text-xs text-slate-500 uppercase tracking-wide">
                  Signed in as
                </div>
                <div className="text-sm text-slate-900 truncate">
                  {user?.email}
                </div>
              </div>
              <button
                type="button"
                data-testid="logout-btn"
                onClick={async () => {
                  setMenuOpen(false);
                  await logout();
                  // Phase 7a — logout returns to the public landing page.
                  navigate("/", { replace: true });
                }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50",
                )}
              >
                <LogOut size={14} aria-hidden="true" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
